import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Chip, Stack, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import color from "../../Constants/colors";
import { formatFileSize } from "./AttachmentList";

// Same limits as the backend (help_desk_attachment_service.py).
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 10;
const EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".txt",
  ".log",
  ".csv",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
];

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

/** Checks new files against the limits; returns the accepted list and an error message key. */
export const useAttachmentFiles = () => {
  const { t } = useTranslation("helpDesk");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");

  const add = (incoming: File[]) => {
    let message = "";
    const accepted = incoming.filter((file) => {
      if (!EXTENSIONS.includes(extensionOf(file.name))) {
        message = t("attachments.unsupported", { name: file.name });
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        message = t("attachments.tooLarge", { name: file.name });
        return false;
      }
      return true;
    });
    const next = [...files, ...accepted];
    if (next.length > MAX_FILES) {
      message = t("attachments.tooMany");
    }
    setFiles(next.slice(0, MAX_FILES));
    setError(message);
  };

  const remove = (index: number) =>
    setFiles((current) => current.filter((_, i) => i !== index));

  const clear = () => {
    setFiles([]);
    setError("");
  };

  return { files, error, add, remove, clear };
};

type Props = {
  files: File[];
  error: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  showHint?: boolean;
};

const AttachmentInput: React.FC<Props> = ({
  files,
  error,
  onAdd,
  onRemove,
  disabled,
  showHint,
}) => {
  const { t } = useTranslation("helpDesk");
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={0.75}>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={EXTENSIONS.join(",")}
          onChange={(e) => {
            onAdd(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        <Button
          size="small"
          startIcon={<AttachFileIcon />}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          sx={{
            color: `${color.secondary} !important`,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {t("attachments.add")}
        </Button>
        {files.map((file, index) => (
          <Chip
            key={`${file.name}-${index}`}
            label={`${file.name} · ${formatFileSize(file.size)}`}
            size="small"
            disabled={disabled}
            onDelete={() => onRemove(index)}
            sx={{
              maxWidth: 240,
              bgcolor: `${color.blue50} !important`,
              color: `${color.primary_dark} !important`,
              fontSize: 11,
            }}
          />
        ))}
      </Stack>
      {error ? (
        <Typography sx={{ color: `${color.red700} !important`, fontSize: 11 }}>
          {error}
        </Typography>
      ) : (
        showHint && (
          <Typography sx={{ color: `${color.gray500} !important`, fontSize: 10.5 }}>
            {t("attachments.hint")}
          </Typography>
        )
      )}
    </Stack>
  );
};

export default AttachmentInput;
